/**
 * Live-Preise (Händler-API der Hauptapp, Tabellen `supplier_integrations` +
 * `live_price_quotes` in der Softec-DB) — LESE-Seite des Konstruktors.
 *
 * Der Konstruktor löst in v1 KEINEN Abruf aus (der Trigger lebt in der Hauptapp/App A);
 * er zeigt vorhandene Quotes an und erlaubt eine BEWUSSTE Übernahme des Live-Preises in
 * die Kalkulation — der Katalogpreis bleibt Default. Reine Bewertungs-/Ableitungslogik
 * ohne DB-/Store-Zugriff, damit alles unter node:test prüfbar ist; die SQL-Seite lebt in
 * server/routes/softec-db.ts, die UI in client/src/components/kalkulation/.
 *
 * Einheiten-Veto-Pflicht (Review-Auflage Grok): ein Live-Quote läuft durch DENSELBEN
 * Umrechnungspfad wie Katalogpreise (shared/effective-unit-price.ts) — priceUnitFactor
 * wird NIE ignoriert (der stille 100x-Fehler). Anders als beim Katalog ("unbekannt ≠
 * Veto") gilt für Quotes die HÄRTERE Regel: eine unbekannte ODER unvereinbare Einheit
 * ⇒ Status 'unsupported-unit', NICHT übernehmbar. Begründung: ein Katalogpreis ist vom
 * Nutzer im Kontext sichtbar und von Hand prüfbar; ein Live-Preis wird als "aktueller,
 * besserer Wert" wahrgenommen und mit einem Klick übernommen — dort darf keine
 * ungeprüfte Einheiten-Vermutung durchrutschen.
 */

import { areUnitsIncompatible, normalizeUnitClass } from './catalog-position-match';
import { effectiveUnitPrice, type EffectiveUnitPriceResult } from './effective-unit-price';
import type { PreisHerkunft } from './schema';

// ============================================
// Typen (DTO wie von GET /api/softec-db/live-quotes geliefert)
// ============================================

/** Status-Werte, wie sie die Hauptapp in live_price_quotes.status schreibt. */
export type LiveQuoteStatus =
  | 'ok'
  | 'nicht-konfiguriert'
  | 'discovery'
  | 'auth'
  | 'timeout'
  | 'rate-limit'
  | 'not-found'
  | 'partial'
  | 'parse-error'
  | 'unsupported-unit'
  | 'stale';

/**
 * Abgeleitete Frische eines Quotes: 'ok' innerhalb der TTL wird zu 'live-frisch',
 * 'ok' nach Ablauf der TTL zu 'stale' — alle anderen Status werden UNVERÄNDERT
 * durchgereicht (kein Umdeuten von Fehlerzuständen).
 */
export type LiveQuoteFrische = 'live-frisch' | LiveQuoteStatus;

export interface LivePriceQuoteDto {
  requestedNumber: string;
  supplierSku: string | null;
  gtin: string | null;
  netPrice: number | null;
  listPrice: number | null;
  currency: string | null;
  priceUnit: string | null;
  orderUnit: string | null;
  priceUnitFactor: number | null;
  qtyFrom: number | null;
  qtyTo: number | null;
  taxIncluded: boolean | null;
  taxRate: number | null;
  validFrom: string | null;
  validTo: string | null;
  fetchedAt: string | null;
  ttlSeconds: number | null;
  quelle: string | null;
  status: string;
  errorCode: string | null;
  quoteId: string | null;
}

/** Integrationsstatus eines Lieferanten (supplier_integrations.status). */
export type LiveIntegrationStatus = 'none' | 'discovery' | 'active' | 'error';

// ============================================
// Frische-Ableitung
// ============================================

/**
 * Leitet die Frische eines Quotes ab (siehe {@link LiveQuoteFrische}). Reine Funktion —
 * `now` explizit, damit Tests deterministisch sind. Ein 'ok'-Quote OHNE brauchbares
 * fetchedAt ist konservativ 'stale' (ein "frisch" ohne belegbaren Zeitstempel wäre
 * geraten). ttlSeconds fehlend/0 ⇒ TTL 0 ⇒ nur ein exakt jetzt geholter Quote wäre
 * frisch — praktisch also 'stale' (die Hauptapp setzt die TTL; fehlt sie, ist Frische
 * nicht belegbar).
 */
export function leiteQuoteFrischeAb(
  quote: Pick<LivePriceQuoteDto, 'status' | 'fetchedAt' | 'ttlSeconds'>,
  now: Date,
): LiveQuoteFrische {
  if (quote.status !== 'ok') return quote.status as LiveQuoteFrische;
  const fetchedMs = quote.fetchedAt ? new Date(quote.fetchedAt).getTime() : Number.NaN;
  if (!Number.isFinite(fetchedMs)) return 'stale';
  const ttlMs = (typeof quote.ttlSeconds === 'number' && quote.ttlSeconds > 0 ? quote.ttlSeconds : 0) * 1000;
  return fetchedMs + ttlMs >= now.getTime() ? 'live-frisch' : 'stale';
}

// ============================================
// Einheiten-Pfad (DERSELBE wie Katalogpreise) + Veto
// ============================================

/**
 * Baut aus priceUnit + priceUnitFactor des Quotes den Preiseinheit-String im
 * KATALOG-Format ("100 M", "ST", …), damit der Quote durch EXAKT denselben
 * Umrechnungspfad läuft wie Katalogpreise (effectiveUnitPrice parst die führende
 * Zahl als Divisor N). Kein eigener Divisions-Code — eine zweite Implementierung
 * wäre genau die Drift, die den historischen EP-Bug (÷10 statt ×1) erzeugt hat.
 */
export function liveQuotePriceUnitString(
  quote: Pick<LivePriceQuoteDto, 'priceUnit' | 'priceUnitFactor'>,
): string | null {
  const unit = (quote.priceUnit ?? '').trim();
  const faktor = quote.priceUnitFactor;
  if (typeof faktor === 'number' && Number.isFinite(faktor) && faktor > 0 && faktor !== 1) {
    return unit ? `${faktor} ${unit}` : String(faktor);
  }
  return unit || null;
}

export interface LiveQuoteBewertung {
  /** Preis PRO EINER Einheit (nach Divisor), oder null ohne nutzbaren Preis. */
  effektiverPreisProEinheit: number | null;
  /** Roh-Ergebnis des geteilten Umrechnungspfads (Divisor n, unitLabel, …). */
  eff: EffectiveUnitPriceResult;
  /** 'unsupported-unit' ⇒ NICHT übernehmbar (hartes Veto, siehe Kopf-Kommentar). */
  einheitStatus: 'ok' | 'unsupported-unit';
  /** true NUR bei einheitStatus 'ok' UND positivem effektivem Preis UND vorhandener quoteId. */
  uebernehmbar: boolean;
  /** Menschlich lesbarer Grund, warum NICHT übernehmbar — null wenn übernehmbar. */
  grund: string | null;
}

/**
 * Einheiten-Veto-Pflicht für Live-Quotes (Review-Auflage Grok — Gegenprobe in
 * tests/live-quote-einheiten-veto.test.ts: Veto entfernen ⇒ Test rot):
 *
 * 1. Der Preis läuft durch effectiveUnitPrice() — priceUnitFactor 100 teilt den
 *    Quote-Preis durch 100, wie beim Katalog. Das ist der Schutz vor dem stillen
 *    100x-Fehler.
 * 2. HARTES Veto (strenger als der Katalog-Pfad): die Einheit des Quotes (unitLabel
 *    aus priceUnit, Fallback orderUnit) MUSS einer bekannten Einheiten-Klasse
 *    angehören UND — wenn die Positions-Einheit ebenfalls bekannt ist — derselben
 *    Klasse wie die Position. Unbekannte Quote-Einheit ⇒ 'unsupported-unit'
 *    (beim Katalog wäre "unbekannt" nur eine weiche Warnung; für den Ein-Klick-
 *    Live-Preis ist Raten nicht akzeptabel).
 */
export function bewerteLiveQuoteEinheit(
  quote: Pick<
    LivePriceQuoteDto,
    'netPrice' | 'listPrice' | 'priceUnit' | 'priceUnitFactor' | 'orderUnit' | 'quoteId'
  >,
  positionEinheit: string | null | undefined,
): LiveQuoteBewertung {
  const eff = effectiveUnitPrice({
    price: quote.netPrice ?? quote.listPrice ?? null,
    priceUnit: liveQuotePriceUnitString(quote),
    quantityUnit: quote.orderUnit,
  });

  const quoteEinheit = eff.unitLabel ?? (quote.orderUnit ?? null);
  const quoteKlasse = normalizeUnitClass(quoteEinheit);
  const unvereinbarMitPosition = areUnitsIncompatible(positionEinheit, quoteEinheit);
  const unsupported = quoteKlasse === null || unvereinbarMitPosition || eff.unitsIncompatible;

  if (unsupported) {
    return {
      effektiverPreisProEinheit: eff.value,
      eff,
      einheitStatus: 'unsupported-unit',
      uebernehmbar: false,
      grund:
        quoteKlasse === null
          ? `Einheit des Live-Preises unbekannt („${quoteEinheit ?? '—'}") — nicht übernehmbar (unsupported-unit).`
          : `Einheit des Live-Preises („${quoteEinheit}") passt nicht zur Positions-Einheit („${positionEinheit ?? '—'}") — nicht übernehmbar (unsupported-unit).`,
    };
  }

  if (eff.value === null || eff.value <= 0) {
    return {
      effektiverPreisProEinheit: eff.value,
      eff,
      einheitStatus: 'ok',
      uebernehmbar: false,
      grund: 'Live-Quote ohne gültigen positiven Preis — nicht übernehmbar.',
    };
  }

  if (!quote.quoteId) {
    return {
      effektiverPreisProEinheit: eff.value,
      eff,
      einheitStatus: 'ok',
      uebernehmbar: false,
      grund: 'Live-Quote ohne quoteId — Herkunft wäre nicht einfrierbar (Snapshot-Pflicht).',
    };
  }

  return {
    effektiverPreisProEinheit: eff.value,
    eff,
    einheitStatus: 'ok',
    uebernehmbar: true,
    grund: null,
  };
}

// ============================================
// Snapshot-Herkunft (Übernahme friert ein)
// ============================================

/**
 * Baut den {@link PreisHerkunft}-Snapshot für eine Live-Preis-Übernahme. Der Aufrufer
 * MUSS vorher {@link bewerteLiveQuoteEinheit} geprüft haben (uebernehmbar === true) —
 * hier wird defensiv erneut auf quoteId bestanden (wirft statt still eine leere ID
 * einzufrieren).
 */
export function baueLivePreisHerkunft(
  quote: Pick<LivePriceQuoteDto, 'quoteId' | 'fetchedAt' | 'priceUnit' | 'priceUnitFactor'>,
  lieferantId: string,
  faktor: number,
): PreisHerkunft {
  if (!quote.quoteId) {
    throw new Error('Live-Preis-Übernahme ohne quoteId — Snapshot-Pflicht verletzt');
  }
  return {
    quelle: 'live',
    lieferant: lieferantId,
    quoteId: quote.quoteId,
    fetchedAt: quote.fetchedAt ?? new Date(0).toISOString(),
    priceUnit: liveQuotePriceUnitString(quote) ?? '',
    faktor,
  };
}
